import isPlainObject from 'lodash/isPlainObject';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import moment from 'moment';
import {
  FACETS_TYPES,
  aggregationSeparator,
  SEARCH_TYPES,
  defaultPaginationSize,
} from './constants';
import { getParametersFromUrl } from '../../../utils/url-utils/urlUtils';
import { CategoryEnum, SearchTypes } from '../../search.types';
import { QueryParamsType } from '../reducers/queryParams.types';

function getDateValue(data: string) {
  const value = decodeURIComponent(data);
  return !!value && moment(value, ['DD-MM-YYYY', 'YYYY']).isValid()
    ? value
    : undefined;
}

function getSortKeyFromQuery(params: { [string: string]: string }) {
  if (params.chrono && params.chrono_inverted) {
    return 'antiChrono';
  }
  if (params.chrono) {
    return 'chrono';
  }
  if (params.sort_nbr_commentaire) {
    return 'commented';
  }
}

const mappings = {
  userQuery: (params: { [string: string]: string }) => {
    if (!params.q) return '';
    try {
      return decodeURIComponent(params.q);
    } catch (error) {
      console.log(error);
      return params.q;
    }
  },
  type: (params: { [string: string]: string }) => {
    const value = params.type as SearchTypes;
    const exists = SEARCH_TYPES.includes(value);
    if (exists) {
      return value;
    }
  },
  excludeMoyensAnnexes: (params: { [string: string]: string }) =>
    params.exclude_moyens,
  eid: (params: { [string: string]: string }) => params.eid,
  lid: (params: { [string: string]: string }) => params.lid,
  oid: (params: { [string: string]: string }) => params.oid,
  onlyTopResults: (params: { [string: string]: string }) =>
    params.only_top_results,
  dates: (params: { [string: string]: string }) => {
    if (!params.date_to && !params.date_from && !params.date_exact) return;

    return {
      to: getDateValue(params.date_to),
      from: getDateValue(params.date_from),
      exact: getDateValue(params.date_exact),
    };
  },
  parties: (params: { [string: string]: string }) => {
    let defendant;
    let plaintiff;
    try {
      if (params.party) {
        const party: { name?: string; type?: string } = JSON.parse(
          decodeURI(params.party),
        );
        if (isPlainObject(party)) {
          plaintiff = party;
        }
      }
      if (params.other_party) {
        const otherParty: { name?: string; type?: string } = JSON.parse(
          decodeURI(params.other_party),
        );
        if (isPlainObject(otherParty)) {
          defendant = otherParty;
        }
      }

      if (!plaintiff && !defendant) return;
      return {
        defendant,
        plaintiff,
      };
    } catch (error) {
      console.log('error', error);
    }
  },
  selectedKeywords: (params: { [string: string]: string }) => {
    if (!params.selected_keywords) return;

    try {
      const arrayKeywords = JSON.parse(
        decodeURIComponent(params.selected_keywords),
      );
      if (Array.isArray(arrayKeywords) && arrayKeywords.length) {
        return arrayKeywords;
      }
    } catch (error) {
      console.log('error', error);
    }
  },
  restrictToLawArticleIds: (params: { [string: string]: string }) => {
    if (params.restrict_to_law_article_ids) {
      try {
        const lawArticleIds = JSON.parse(
          decodeURIComponent(params.restrict_to_law_article_ids),
        );
        if (Array.isArray(lawArticleIds) && lawArticleIds.length) {
          return lawArticleIds;
        }
      } catch (error) {
        console.log('error', error);
      }
      // Deprecated API
    } else if (
      params.law_article_id &&
      params.filter_on_specific_article_version === 'true'
    ) {
      return [params.law_article_id];
    }
  },
  aggregationsPaths: (params: { [string: string]: string }) => {
    const cats: {
      [key in CategoryEnum]?: Array<Array<string> | string>;
    } = {};

    FACETS_TYPES.forEach(key => {
      if (params[key]) {
        try {
          const arrayValues: {
            [key in CategoryEnum]?: Array<Array<string> | string>;
          } = JSON.parse(decodeURIComponent(params[key]));
          if (Array.isArray(arrayValues) && arrayValues.length) {
            cats[key] = arrayValues;
          }
        } catch (error) {
          console.log('error', error);
        }
      }
    });

    const keys = Object.keys(cats) as Array<keyof typeof cats>;

    if (!keys.length) return;

    return keys.reduce((acc, key) => {
      const catItems = cats[key]!.map(nodes =>
        [key as string].concat(nodes).join(aggregationSeparator),
      );
      return acc.concat(catItems);
    }, <Array<string>>[]);
  },
  sort: (params: { [string: string]: string }) => getSortKeyFromQuery(params),
  originalQueryKey: (params: { [string: string]: string }) =>
    params.original_query_key,
  disableAutocorrect: (params: { [string: string]: string }) =>
    params.disable_autocorrect,
  size: (params: { [string: string]: string }) => {
    const position = parseInt(params.position, 10);
    if (!Number.isNaN(position)) {
      return Math.max(position, defaultPaginationSize); // prevent position:0 -> size: 0
    }
  },
};

export function convertUrlParamsToSearchState(
  url: string,
): Partial<QueryParamsType> {
  const urlParameters = getParametersFromUrl(url);

  const results = {
    userQuery: mappings.userQuery(urlParameters),
    type: mappings.type(urlParameters),
    excludeMoyensAnnexes: mappings.excludeMoyensAnnexes(urlParameters),
    eid: mappings.eid(urlParameters),
    lid: mappings.lid(urlParameters),
    oid: mappings.oid(urlParameters),
    onlyTopResults: mappings.onlyTopResults(urlParameters),
    dates: mappings.dates(urlParameters),
    parties: mappings.parties(urlParameters),
    selectedKeywords: mappings.selectedKeywords(urlParameters),
    aggregationsPaths: mappings.aggregationsPaths(urlParameters),
    sort: mappings.sort(urlParameters),
    originalQueryKey: mappings.originalQueryKey(urlParameters),
    disableAutocorrect: mappings.disableAutocorrect(urlParameters),
    restrictToLawArticleIds: mappings.restrictToLawArticleIds(urlParameters),
    size: mappings.size(urlParameters),
  };

  (Object.keys(results) as Array<keyof typeof results>).forEach(key => {
    if (!results[key]) {
      delete results[key];
    }
  });

  return omitBy(results, isNil);
}
